/**
 * ViewFeedbackModal.tsx
 * Modal to display full feedback/contact message details with attachment
 */

import React, { useEffect, useState } from 'react';
import { X, Mail, Phone, Clock, FileText, Calendar, User, MessageSquare, Download, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatStoredDate } from '@/utils/formatStoredDate';
import { getApiEndpoint, getMediaUrl } from '@/config/api';

type FeedbackDetail = {
  id: number;
  name: string;
  email: string;
  mobile: string;
  subject: string;
  message: string;
  preferred_contact: string;
  best_time: string;
  attachURL: string | null;
  created_at: string;
  processed: boolean;
  processed_at: string | null;
  notes: string | null;
  user_code: string | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  feedbackId: number | null;
};

export default function ViewFeedbackModal({ open, onClose, feedbackId }: Props) {
  const [feedback, setFeedback] = useState<FeedbackDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (open && feedbackId) {
      fetchFeedback();
    }
  }, [open, feedbackId]);

  async function fetchFeedback() {
    if (!feedbackId) return;

    setLoading(true);
    try {
      const response = await fetch(getApiEndpoint(`/admin/feedback/${feedbackId}`));
      const result = await response.json();

      if (result.success) {
        setFeedback(result.data);
      } else {
        toast.error(result.message || 'Failed to fetch feedback');
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
      toast.error('Error loading feedback');
    } finally {
      setLoading(false);
    }
  }

  const handleDownloadAttachment = () => {
    if (feedback?.attachURL) {
      // attachURL is stored as /ContactUs/filename.pdf
      // Backend serves static files from public folder, so construct the full URL
      const baseUrl = getApiEndpoint('').replace('/api', ''); // Remove /api from base URL
      const url = `${baseUrl}${feedback.attachURL}`;
      window.open(url, '_blank');
    }
  };

  async function handleMarkAsProcessed() {
    if (!feedbackId || !feedback) return;

    setUpdating(true);
    try {
      const response = await fetch(getApiEndpoint(`/admin/feedback/${feedbackId}/process`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Feedback marked as processed');
        // Update local state
        setFeedback({
          ...feedback,
          processed: true,
          processed_at: new Date().toISOString()
        });
      } else {
        toast.error(result.message || 'Failed to update feedback');
      }
    } catch (error) {
      console.error('Error updating feedback:', error);
      toast.error('Error updating feedback status');
    } finally {
      setUpdating(false);
    }
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="View feedback modal"
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8"
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        aria-hidden="true"
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
      />

      {/* Modal */}
      <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-white">Feedback Details</h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-gray-500">Loading feedback...</p>
              </div>
            </div>
          ) : feedback ? (
            <div className="space-y-6">
              {/* Status Badge */}
              {feedback.processed && (
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded-lg">
                    ✓ Processed
                  </span>
                  {feedback.processed_at && (
                    <span className="text-sm text-gray-600">
                      on {new Date(feedback.processed_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              )}

              {/* User Information */}
              <div className="bg-gray-50 rounded-xl p-5 space-y-4">
                <h4 className="font-semibold text-gray-900 text-lg mb-3">Contact Information</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                      <User className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Name</p>
                      <p className="font-semibold text-gray-900">{feedback.name}</p>
                    </div>
                  </div>

                  {feedback.email && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Mail className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Email</p>
                        <p className="font-medium text-gray-900 break-all">{feedback.email}</p>
                      </div>
                    </div>
                  )}

                  {feedback.mobile && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Phone className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Mobile</p>
                        <p className="font-medium text-gray-900">{feedback.mobile}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Submitted On</p>
                      <p className="font-medium text-gray-900">
                        {formatStoredDate(feedback.created_at)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Preferences */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Preferred Contact</p>
                    <p className="font-medium text-gray-900 capitalize">{feedback.preferred_contact}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Best Time</p>
                    <p className="font-medium text-gray-900 capitalize">{feedback.best_time}</p>
                  </div>
                </div>

                {feedback.user_code && (
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">User Code</p>
                    <p className="font-medium text-gray-900">{feedback.user_code}</p>
                  </div>
                )}
              </div>

              {/* Subject */}
              {feedback.subject && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-gray-600" />
                    Subject
                  </h4>
                  <p className="text-gray-800 bg-gray-50 rounded-lg p-4">{feedback.subject}</p>
                </div>
              )}

              {/* Message */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-gray-600" />
                  Message
                </h4>
                <div className="text-gray-800 bg-gray-50 rounded-lg p-4 whitespace-pre-wrap">
                  {feedback.message}
                </div>
              </div>

              {/* Attachment */}
              {feedback.attachURL && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-gray-600" />
                    Attachment
                  </h4>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleDownloadAttachment}
                      className="flex items-center gap-2 px-4 py-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <Download className="w-5 h-5" />
                      <span className="font-medium">View Attachment</span>
                      <span className="text-sm text-blue-600">
                        ({feedback.attachURL.split('/').pop()})
                      </span>
                    </button>
                    {!feedback.processed && (
                      <button
                        onClick={handleMarkAsProcessed}
                        disabled={updating}
                        className="flex items-center gap-2 px-4 py-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {updating ? (
                          <>
                            <div className="w-5 h-5 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                            <span className="font-medium">Updating...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-5 h-5" />
                            <span className="font-medium">Mark as Processed</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Notes (if any) */}
              {feedback.notes && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Admin Notes</h4>
                  <div className="text-gray-800 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    {feedback.notes}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">Feedback not found</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="flex items-center gap-3">
            {!feedback?.processed && !feedback?.attachURL && (
              <button
                onClick={handleMarkAsProcessed}
                disabled={updating}
                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {updating ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>Mark as Processed</span>
                  </>
                )}
              </button>
            )}
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
